/**
 * Aktivite sözleşmeleri (M4 WP 4.1/4.2). Kaynak: keşif tur 2 F10/F11 (form alanları, tamamlama = ilerleme %100, tekrarın
 * tamamlamada tek sonraki oluşum olarak üretilmesi), ÜK-10, kararlar `docs/planning/m4-decisions-2026-09-29.md` K-M4-10…17.
 * Migration 0057.
 *
 * Zaman modeli: planlanan başlangıç/bitiş tenant saat diliminde YEREL tarih (+ isteğe bağlı saat). UTC anları (`startsAt`,
 * `endsAt`) bu dosyadaki yardımcılarla türetilir; tekrar duvar saatiyle hesaplanır (09:00 yaz saati geçişinde de 09:00).
 */
import { z } from 'zod';
import { latitudeSchema, longitudeSchema } from './geo.js';
import { recordOriginSchema } from './record-origin.js';
import { localToUtc } from './work-calendar.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'tarih YYYY-AA-GG olmalı');
const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'saat SS:DD olmalı');
const optText = (max) => z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === '' ? null : s))
    .nullable();
// ---------------------------------------------------------------------------------------------------------------------
// Sabit kümeler
// ---------------------------------------------------------------------------------------------------------------------
export const ACTIVITY_KINDS = ['task', 'call', 'meeting', 'visit', 'event', 'other'];
export const ACTIVITY_KIND_LABELS = {
    task: 'İş',
    call: 'Çağrı',
    meeting: 'Toplantı',
    visit: 'Ziyaret',
    event: 'Etkinlik',
    other: 'Diğer',
};
export const ACTIVITY_PRIORITIES = ['low', 'normal', 'high', 'critical'];
export const ACTIVITY_PRIORITY_LABELS = {
    low: 'Düşük',
    normal: 'Normal',
    high: 'Yüksek',
    critical: 'Kritik',
};
/** Renk anahtarları (K-M4-10): arayüz tasarım belirteçlerine eşler; ham renk kodu saklanmaz. */
export const ACTIVITY_COLORS = [
    'slate',
    'blue',
    'green',
    'amber',
    'red',
    'purple',
    'teal',
    'pink',
];
export const ACTIVITY_COLOR_LABELS = {
    slate: 'Gri',
    blue: 'Mavi',
    green: 'Yeşil',
    amber: 'Sarı',
    red: 'Kırmızı',
    purple: 'Mor',
    teal: 'Turkuaz',
    pink: 'Pembe',
};
/** Tekrar seçenekleri (keşif F10: 11 seçenek). */
export const ACTIVITY_REPEATS = [
    'none',
    'daily',
    'every_3_days',
    'weekly',
    'biweekly',
    'monthly',
    'bimonthly',
    'quarterly',
    'every_4_months',
    'semiannual',
    'yearly',
];
export const ACTIVITY_REPEAT_LABELS = {
    none: 'Tekrar yok',
    daily: 'Her gün',
    every_3_days: 'Her 3 günde bir',
    weekly: 'Her hafta',
    biweekly: 'Her 2 haftada bir',
    monthly: 'Her ay',
    bimonthly: 'Her 2 ayda bir',
    quarterly: 'Her 3 ayda bir',
    every_4_months: 'Her 4 ayda bir',
    semiannual: 'Her 6 ayda bir',
    yearly: 'Her yıl',
};
/** Periyot: gün ya da ay adımı (yıl = 12 ay; ay periyotları çapa gününe göre ay sonuna kırpılır). */
export const ACTIVITY_REPEAT_STEP = {
    daily: { days: 1 },
    every_3_days: { days: 3 },
    weekly: { days: 7 },
    biweekly: { days: 14 },
    monthly: { months: 1 },
    bimonthly: { months: 2 },
    quarterly: { months: 3 },
    every_4_months: { months: 4 },
    semiannual: { months: 6 },
    yearly: { months: 12 },
};
// ---------------------------------------------------------------------------------------------------------------------
// Tarih yardımcıları (saf; YYYY-AA-GG metni üzerinde, saat dilimsiz)
// ---------------------------------------------------------------------------------------------------------------------
const DAY_MS = 86_400_000;
function parts(iso) {
    const [y, m, d] = iso.split('-').map(Number);
    return { y, m, d };
}
function isoOf(y, m, d) {
    return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function monthDays(y, m) {
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
export function addDaysIso(iso, days) {
    const { y, m, d } = parts(iso);
    const t = new Date(Date.UTC(y, m - 1, d) + days * DAY_MS);
    return isoOf(t.getUTCFullYear(), t.getUTCMonth() + 1, t.getUTCDate());
}
/** b − a (gün). */
export function daysBetweenIso(a, b) {
    const pa = parts(a);
    const pb = parts(b);
    return Math.round((Date.UTC(pb.y, pb.m - 1, pb.d) - Date.UTC(pa.y, pa.m - 1, pa.d)) / DAY_MS);
}
/** Ay adımı: hedef ayda gün = çapa günü, ay sonuna kırpılır (31 Oca → 28/29 Şub → 31 Mar). */
export function addMonthsAnchoredIso(iso, months, anchorDay) {
    const { y, m } = parts(iso);
    const idx = y * 12 + (m - 1) + months;
    const ny = Math.floor(idx / 12);
    const nm = (idx % 12) + 1;
    return isoOf(ny, nm, Math.min(anchorDay, monthDays(ny, nm)));
}
/**
 * Sonraki oluşumun tarihleri (ÜK-10, K-M4-12): önceki PLANLANAN tarih + periyot; bitiş aynı gün farkıyla kayar. Geçmişe
 * düşse de tek sonraki oluşum döner (kaçırılan dönemler topluca üretilmez). Hafta sonu / kapalı gün atlanmaz.
 */
export function nextOccurrenceDates(a) {
    if (a.repeat === 'none')
        return null;
    const step = ACTIVITY_REPEAT_STEP[a.repeat];
    let start;
    if ('days' in step)
        start = addDaysIso(a.startOn, step.days);
    else {
        const anchorDay = parts(a.anchorOn ?? a.startOn).d;
        start = addMonthsAnchoredIso(a.startOn, step.months, anchorDay);
    }
    const endOn = a.endOn === null ? null : addDaysIso(start, daysBetweenIso(a.startOn, a.endOn));
    return { startOn: start, endOn };
}
function minutesOfTime(t) {
    const [h, mm] = t.split(':').map(Number);
    return h * 60 + mm;
}
/** Planlanan başlangıcın UTC anı (saatsiz = yerel gün başı). */
export function activityStartsAt(tz, startOn, startTime) {
    const { y, m, d } = parts(startOn);
    return new Date(localToUtc(tz, y, m, d, startTime === null ? 0 : minutesOfTime(startTime)));
}
/** Planlanan bitişin UTC anı: saatli → o an; saatsiz → bitiş gününün sonu (ertesi yerel gün başı); bitiş yoksa `null`. */
export function activityEndsAt(tz, endOn, endTime) {
    if (endOn === null)
        return null;
    if (endTime !== null) {
        const { y, m, d } = parts(endOn);
        return new Date(localToUtc(tz, y, m, d, minutesOfTime(endTime)));
    }
    const { y, m, d } = parts(addDaysIso(endOn, 1));
    return new Date(localToUtc(tz, y, m, d, 0));
}
/** Tenant saat diliminde bir anın yerel tarihi (YYYY-AA-GG). */
export function localDateIso(tz, at) {
    const p = {};
    for (const x of new Intl.DateTimeFormat('en-CA', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(at))
        if (x.type !== 'literal')
            p[x.type] = x.value;
    return `${p['year'] ?? '0000'}-${p['month'] ?? '01'}-${p['day'] ?? '01'}`;
}
/** Tarih/saat tutarlılığı (DB kısıtıyla aynı kural): hata alanı ve iletisi ya da `null`. */
export function activityDatesError(d) {
    if (d.endTime !== null && d.endOn === null)
        return { field: 'endOn', message: 'bitiş saati için bitiş tarihi gerekli' };
    if (d.endOn !== null) {
        if (d.endOn < d.startOn)
            return { field: 'endOn', message: 'bitiş başlangıçtan önce olamaz' };
        if (d.endOn === d.startOn &&
            d.startTime !== null &&
            d.endTime !== null &&
            d.endTime < d.startTime)
            return { field: 'endTime', message: 'bitiş saati başlangıçtan önce olamaz' };
    }
    return null;
}
// ---------------------------------------------------------------------------------------------------------------------
// Kuşbakışı (K-M4-15): başlangıç tarihine göre kolonlar
// ---------------------------------------------------------------------------------------------------------------------
export const ACTIVITY_BOARD_COLUMNS = [
    'previous',
    'yesterday',
    'today',
    'tomorrow',
    'later',
];
export const ACTIVITY_BOARD_COLUMN_LABELS = {
    previous: 'Önceki',
    yesterday: 'Dün',
    today: 'Bugün',
    tomorrow: 'Yarın',
    later: 'Sonrası',
};
export function boardColumnOf(startOn, today) {
    const diff = daysBetweenIso(today, startOn);
    if (diff < -1)
        return 'previous';
    if (diff === -1)
        return 'yesterday';
    if (diff === 0)
        return 'today';
    if (diff === 1)
        return 'tomorrow';
    return 'later';
}
/**
 * Kolona taşımanın hedef tarihi (PM, K-M4-15): Dün/Bugün/Yarın → o gün; Sonrası → yarından sonraki gün. "Önceki" hedef
 * olamaz (belirsiz geçmiş tarih üretilmez) → `null`.
 */
export function boardTargetDate(column, today) {
    switch (column) {
        case 'yesterday':
            return addDaysIso(today, -1);
        case 'today':
            return today;
        case 'tomorrow':
            return addDaysIso(today, 1);
        case 'later':
            return addDaysIso(today, 2);
        case 'previous':
            return null;
    }
}
// ---------------------------------------------------------------------------------------------------------------------
// Girdi şemaları
// ---------------------------------------------------------------------------------------------------------------------
export const activityAssigneeSchema = z.object({
    type: z.enum(['user', 'dept']),
    id: uuid,
});
export const activityLocationSchema = z
    .object({
    label: optText(200).optional(),
    lat: latitudeSchema,
    lng: longitudeSchema,
})
    .strict();
export const activityCoreInputSchema = z
    .object({
    kind: z.enum(ACTIVITY_KINDS),
    name: z.string().trim().min(1, 'ad gerekli').max(200),
    color: z.enum(ACTIVITY_COLORS).nullable().optional(),
    description: optText(10000).optional(),
    priority: z.enum(ACTIVITY_PRIORITIES).optional(),
    startOn: isoDate,
    startTime: hhmm.nullable().optional(),
    endOn: isoDate.nullable().optional(),
    endTime: hhmm.nullable().optional(),
    repeat: z.enum(ACTIVITY_REPEATS).optional(),
    assignees: z.array(activityAssigneeSchema).min(1, 'en az bir görevli gerekli').max(20),
    location: activityLocationSchema.nullable().optional(),
    publishToFeed: z.boolean().optional(),
    ownerId: uuid.optional(),
})
    .strict();
export const createActivityInputSchema = z
    .object({
    core: activityCoreInputSchema,
    /** Asıl ilgili kayıt(lar). `separatePerPrimary` kapalıyken en çok bir kayıt. */
    primaryRecordIds: z.array(uuid).max(50).default([]),
    /** "Her asıl ilgili için ayrı bir aktivite oluştur" (keşif F10 `MultipleTasks`) */
    separatePerPrimary: z.boolean().default(false),
    relatedRecordIds: z.array(uuid).max(20).default([]),
    subtasks: z.array(z.string().trim().min(1).max(300)).max(100).default([]),
    /**
     * İstek anahtarı (niyet başına bir belirteç): aynı belirteç + aynı içerik yeni kayıt üretmez (ilk kayıt döner); aynı
     * belirteç + farklı içerik 409 (`ACTIVITY_CREATE_REFUSAL_REASONS`). Sözleşme: docs/spec/castinflow/activities.md.
     */
    clientToken: uuid.optional(),
    customValues: z.record(z.string(), z.unknown()).prefault({}),
})
    .strict();
/**
 * Toplu aktivite oluşturma (v4.6; docs/spec/castinflow/satis-potansiyeli.md § 4 "Takip görevi"): "her biri için ayrı" seçeneğiyle
 * BİRDEN ÇOK asıl ilgiliye ayrı ayrı aktivite (ör. satış takibi ve aday/fırsat listesindeki toplu takip görevi). Liste toplu
 * işlemleriyle TEK yetki kuralına bağlıdır: kayıt yazma + `records.bulk` + kullanıcı başına ortak toplu hız sınırı + her hedef
 * kayıtta kayıt düzeyi YAZMA yetkisi. Tek asıl ilgili (ya da hiç) toplu değildir ve bugünkü kuralında kalır (okunabilen kayda
 * bağlanır). Kimlikler büyük/küçük harften bağımsız tekilleştirilir (sunucu da öyle yapar).
 */
export function isBulkActivityCreate(input) {
    return (input.separatePerPrimary === true &&
        new Set(input.primaryRecordIds.map((x) => x.toLowerCase())).size > 1);
}
/**
 * Toplu aktivite oluşturma bu yeteneklerle kullanılabilir (`records.bulk` + kayıt yazma). Arayüz yalnız kullanılabilirlik için
 * bakar; yetki sınırı API'dedir (uç + servis, her hedef kayıt yeniden denetlenir).
 */
export function canBulkCreateActivities(capabilities) {
    const caps = capabilities ?? [];
    return (caps.includes('records.bulk') &&
        (caps.includes('records.write') || caps.includes('records.write.lite')));
}
// ---------------------------------------------------------------------------------------------------------------------
// İstek anahtarı sözleşmesi (oluşturmada tekrar güvenliği; migration 20261012_0205)
// ---------------------------------------------------------------------------------------------------------------------
/**
 * `POST /records/activity` + `clientToken` ret gerekçeleri (409 `CONFLICT`, `details.reason`):
 *  - `request_key_reused`: anahtar AYNI kullanıcının önceki isteğinde FARKLI içerikle kullanıldı. İlk kayıt değişmez, yenisi
 *    oluşmaz.
 *  - `request_key_unavailable`: anahtar bu kullanıcı için kullanılamaz — başka kullanıcının isteğinde kullanılmış, onunla
 *    oluşan kayıt silinmiş ya da kullanıcı o kaydı artık göremiyor. Hangisi olduğu BİLDİRİLMEZ; kayıt kimliği/içeriği dönmez.
 */
export const ACTIVITY_CREATE_REFUSAL_REASONS = [
    'request_key_reused',
    'request_key_unavailable',
];
export const ACTIVITY_CREATE_REFUSAL_TEXT = {
    request_key_reused: 'Bu form daha önce farklı içerikle gönderildi ve kayıt o içerikle oluşturuldu; sonraki değişiklikler kaydedilmedi. Formu kapatıp oluşan kaydı kontrol edin; farklı bir kayıt için formu yeniden açın.',
    request_key_unavailable: 'Bu istek anahtarı kullanılamıyor; yeni kayıt oluşturulmadı. Formu kapatıp yeniden açarak tekrar deneyin.',
};
/** Anahtarları sıralı JSON değeri (özel alan değerleri: alan sırası özeti değiştirmez; dizi sırası anlamlıdır, korunur) */
function sortedJson(v) {
    if (Array.isArray(v))
        return v.map(sortedJson);
    if (v !== null && typeof v === 'object') {
        const o = v;
        const out = {};
        for (const k of Object.keys(o).sort())
            out[k] = sortedJson(o[k]);
        return out;
    }
    return v;
}
/**
 * Oluşturma isteğinin İÇERİK özeti kaynağı (sunucu SHA-256'sını `create_request_hash` olarak saklar). Aynı niyetin yeniden
 * gönderimi (çift tıklama, ağ yeniden denemesi) AYNI metni üretir; kullanıcının değiştirdiği her alan metni değiştirir.
 * Özeti değiştirmeyenler: `clientToken`, alan sırası, kimliklerin büyük/küçük harfi, görevli ve ilgili listelerinin sırası
 * ve tekrarı, verilmeyen alan ile varsayılanı (`priority`, `repeat`, boş bırakılan isteğe bağlı alanlar).
 */
export function activityCreateFingerprint(input) {
    const c = input.core;
    const ids = (list) => [...new Set(list.map((x) => x.toLowerCase()))].sort();
    const loc = c.location ?? null;
    return JSON.stringify([
        'activity.create/1',
        c.kind,
        c.name,
        c.color ?? null,
        c.description ?? null,
        c.priority ?? 'normal',
        c.startOn,
        c.startTime ?? null,
        c.endOn ?? null,
        c.endTime ?? null,
        c.repeat ?? 'none',
        ids(c.assignees.map((a) => `${a.type}:${a.id}`)),
        loc === null ? null : [loc.label ?? null, loc.lat, loc.lng],
        c.publishToFeed ?? null,
        c.ownerId?.toLowerCase() ?? null,
        ids(input.primaryRecordIds),
        input.separatePerPrimary,
        ids(input.relatedRecordIds),
        input.subtasks,
        sortedJson(input.customValues),
    ]);
}
export const updateActivityInputSchema = z
    .object({
    core: activityCoreInputSchema.partial(),
    primaryRecordId: uuid.nullable().optional(),
    relatedRecordIds: z.array(uuid).max(20).optional(),
    customValues: z.record(z.string(), z.unknown()).optional(),
})
    .strict();
/**
 * Yalnız ilerleme. v4.6: uç gövdesi isteğe bağlı satış potansiyeli değerlendirmesini de taşır — sunucu
 * `activityCompletionInputSchema` (`sales-assessment.ts`) ile doğrular.
 */
export const activityProgressInputSchema = z
    .object({ progress: z.number().int().min(0).max(100) })
    .strict();
export const activityBulkCompleteInputSchema = z
    .object({ ids: z.array(uuid).min(1).max(200) })
    .strict();
export const activityMoveInputSchema = z
    .object({ column: z.enum(ACTIVITY_BOARD_COLUMNS) })
    .strict();
export const activitySubtaskCreateSchema = z
    .object({ title: z.string().trim().min(1).max(300) })
    .strict();
export const activitySubtaskUpdateSchema = z
    .object({ title: z.string().trim().min(1).max(300).optional(), done: z.boolean().optional() })
    .strict();
export const activityRangeQuerySchema = z
    .object({
    from: isoDate,
    to: isoDate,
    /** Yalnız bana atanmışlar */
    mine: z.enum(['0', '1']).optional(),
})
    .strict()
    .refine((q) => q.to >= q.from && daysBetweenIso(q.from, q.to) <= 62, {
    message: 'aralık en çok 62 gün',
    path: ['to'],
});
// ---------------------------------------------------------------------------------------------------------------------
// Çıktı şemaları
// ---------------------------------------------------------------------------------------------------------------------
const linkRef = z.object({
    id: z.string(),
    type: z.string(),
    title: z.string(),
    isPrimary: z.boolean(),
});
export const activityProfileSchema = z.object({
    kind: z.literal('activity'),
    activityKind: z.enum(ACTIVITY_KINDS),
    name: z.string(),
    color: z.enum(ACTIVITY_COLORS).nullable(),
    description: z.string().nullable(),
    priority: z.enum(ACTIVITY_PRIORITIES),
    startOn: z.string(),
    startTime: z.string().nullable(),
    endOn: z.string().nullable(),
    endTime: z.string().nullable(),
    startsAt: z.string(),
    endsAt: z.string().nullable(),
    repeat: z.enum(ACTIVITY_REPEATS),
    progress: z.number().int(),
    completedAt: z.string().nullable(),
    completedBy: z.string().nullable(),
    /** Tamamlanma süresi (dakika): planlanan başlangıçtan tamamlamaya; tamamlanmamışsa `null` */
    completionMinutes: z.number().int().nullable(),
    location: z.object({ label: z.string().nullable(), lat: z.number(), lng: z.number() }).nullable(),
    publishToFeed: z.boolean(),
    assignees: z.array(activityAssigneeSchema),
    /** Yalnız çağıranın görebildiği ilgili kayıtlar (görünmeyenin adı/kimliği dönmez) */
    links: z.array(linkRef),
    hiddenLinkCount: z.number().int(),
    subtasks: z.array(z.object({
        id: z.string(),
        position: z.number().int(),
        title: z.string(),
        doneAt: z.string().nullable(),
        doneBy: z.string().nullable(),
    })),
    recurrence: z.object({
        /** Bu oluşumu üreten (tamamlanmış) oluşum */
        sourceId: z.string().nullable(),
        /** Bu oluşum tamamlanınca üretilen sonraki oluşum (görünürse) */
        nextId: z.string().nullable(),
    }),
    sourceStep: z
        .object({ enrollmentId: z.string().nullable(), position: z.number().int() })
        .nullable(),
    /** EK1: ajan önerisinden onayla oluştuysa köken (önceki sürüm alanı göndermez) */
    origin: recordOriginSchema.nullable().default(null),
});
export const activityEntrySchema = z.object({
    id: z.string(),
    name: z.string(),
    activityKind: z.enum(ACTIVITY_KINDS),
    color: z.enum(ACTIVITY_COLORS).nullable(),
    priority: z.enum(ACTIVITY_PRIORITIES),
    startOn: z.string(),
    startTime: z.string().nullable(),
    endOn: z.string().nullable(),
    endTime: z.string().nullable(),
    startsAt: z.string(),
    endsAt: z.string().nullable(),
    progress: z.number().int(),
    completedAt: z.string().nullable(),
    /** Asıl ilgili kayıt (görünürse) */
    primary: z.object({ id: z.string(), type: z.string(), title: z.string() }).nullable(),
    /**
     * K-M7-50.5: görevliler (kullanıcı/departman adıyla) — kuşbakışı ve liste kartlarında saatle birlikte gösterilir. Eski
     * istemci/yanıtlarla uyum için yoksa boş liste.
     */
    assignees: z
        .array(z.object({ type: z.enum(['user', 'dept']), id: z.string(), name: z.string() }))
        .default([]),
    /** EK1: takvimde köken etiketi (ajan önerisinden onayla oluştuysa; önceki sürüm alanı göndermez) */
    origin: recordOriginSchema.nullable().default(null),
    /**
     * Z6 (Bugün → konuşma → müşteri bütünlüğü): YALNIZ kayıt sayfası uçlarında (`GET /activities/linked/:id`) dolar. İş kayda doğrudan
     * değil, kayda bağlı (aday/kişi) konuşma üzerinden geliyorsa o konuşmalar (çağırana görünenler; ad = konuşma başlığı). İş kayda
     * doğrudan da bağlıysa boş liste (tek iş, kaynak "doğrudan"). Önceki sürüm alanı göndermez.
     */
    viaConversations: z.array(z.object({ id: z.string(), title: z.string() })).default([]),
});
/**
 * Z6 (PM incelemesi 04.11): kayıt sayfasının bağlı işleri SAYFALI — `GET /activities/linked/:id?limit=&cursor=`. Sıra
 * `starts_at DESC, id DESC`; kapsam (aday/kişide bağlı okunabilen konuşmaların işleri dahil), konuşma ve aktivite okuma yüklemi,
 * tekrar giderme ve sıra AYNI SQL'de, `LIMIT`'ten ÖNCE uygulanır (görünmeyen iş pencereyi tüketemez). İmleç opaktır, yalnız
 * çağıranın gördüğü son satırın konumunu taşır.
 */
export const ACTIVITY_LINKED_PAGE_MAX = 200;
export const activityLinkedQuerySchema = z.object({
    limit: z.coerce.number().int().min(1).max(ACTIVITY_LINKED_PAGE_MAX).optional(),
    cursor: z.string().min(1).max(200).optional(),
});
export const activityLinkedPageSchema = z.object({
    items: z.array(activityEntrySchema),
    /** Sonraki sayfanın imleci; daha fazla iş yoksa `null` (önceki sürüm alanı göndermez → `null`) */
    nextCursor: z.string().nullable().default(null),
});
/**
 * Z6: kayıt özetinin "Sonraki iş", "Son temas" ve "Yakın geçmiş" bilgileri — sunucuda, kaydın TÜM erişilebilir iş kapsamından
 * (ilk sayfadan değil) `GET /activities/linked/:id/summary`. Kurallar:
 *  - sonraki iş: tamamlanmamış işlerden başlangıç günü en erken (eşitse saati olan ve erken olan, saatsiz sona; sonra ad, kimlik);
 *    `nextWorkOverdue` başlangıç günü çalışma alanının bugününden (`today`, kiracı saat dilimi) önceyse;
 *  - son temas: iş/görev dışında tamamlanmış aktivitelerden tamamlanma anı en yeni (liste alanı `lastContactAt` ile aynı);
 *  - yakın geçmiş: tamamlanmış aktiviteler, tamamlanma anı en yeniden (en çok 3).
 */
export const activityLinkedSummarySchema = z.object({
    /** Çalışma alanının (kiracı saat dilimi) bugünü, `YYYY-MM-DD` — gecikme bu güne göre */
    today: z.string(),
    nextWork: activityEntrySchema.nullable(),
    nextWorkOverdue: z.boolean(),
    lastContact: activityEntrySchema.nullable(),
    recent: z.array(activityEntrySchema).max(3),
});
export const activityRangeResponseSchema = z.object({
    items: z.array(activityEntrySchema),
    /** Sunucu tavanına ulaşıldı: pencerede daha fazla kayıt var */
    truncated: z.boolean(),
    timeZone: z.string(),
});
export const activityBoardResponseSchema = z.object({
    today: z.string(),
    timeZone: z.string(),
    columns: z.object({
        previous: z.array(activityEntrySchema),
        yesterday: z.array(activityEntrySchema),
        today: z.array(activityEntrySchema),
        tomorrow: z.array(activityEntrySchema),
        later: z.array(activityEntrySchema),
    }),
    truncated: z.boolean(),
});
export const activityMapResponseSchema = z.object({
    items: z.array(activityEntrySchema.extend({
        location: z.object({ label: z.string().nullable(), lat: z.number(), lng: z.number() }),
    })),
    /** Pencerede konumsuz (haritada gösterilemeyen) görünür aktivite sayısı */
    withoutLocation: z.number().int(),
    truncated: z.boolean(),
});
/** Haber akışı sağ paneli aktivite bölümleri (K-M4-16). */
export const activityPanelResponseSchema = z.object({
    /** Bana atanmış, başlamış ve tamamlanmamış aktiviteler */
    nowActive: z.array(activityEntrySchema),
    /** Bana atanmış, ileri tarihli açık aktiviteler */
    later: z.array(activityEntrySchema),
    /** Bana atanmış açık aktivitelerin tamamlanmamış "yapılacak işleri" (alt görevler) */
    todos: z.array(z.object({
        id: z.string(),
        title: z.string(),
        activity: z.object({ id: z.string(), name: z.string() }),
    })),
    reminders: z.array(z.object({
        id: z.string(),
        dueAt: z.string(),
        note: z.string().nullable(),
        record: z.object({ id: z.string(), type: z.string(), title: z.string() }),
    })),
});
export const activityBulkCompleteResponseSchema = z.object({
    completed: z.number().int(),
    alreadyCompleted: z.number().int(),
    /** Yazma yetkisi olmayan ya da bulunamayan kayıtlar (atlandı) */
    skipped: z.number().int(),
    /** Tekrarla üretilen sonraki oluşum sayısı */
    nextCreated: z.number().int(),
    /**
     * v4.6 satış potansiyeli: kapsamdaki zorunlu değerlendirme eksik olduğu için TAMAMLANMAYAN aktiviteler (diğerleri tamamlanır).
     * Önceki sürüm alanı göndermez.
     */
    blocked: z
        .array(z.object({ id: z.string(), reason: z.enum(['assessment_required']) }))
        .default([]),
});
export const createActivityResponseSchema = z.object({
    /** Oluşturulan (ya da aynı `clientToken` ile daha önce oluşturulmuş) aktiviteler */
    ids: z.array(z.string()),
    /** Aynı belirteçle önceki istek bulundu; yeni kayıt üretilmedi */
    replayed: z.boolean(),
});
export const activityNeighborsResponseSchema = z.object({
    prevId: z.string().nullable(),
    nextId: z.string().nullable(),
});
//# sourceMappingURL=activities.js.map